// MarketingAgent — UI del Agente de Diseño y Comunicación.
// Nico escribe un brief, el agente decide qué piezas crear y guarda borradores en posts_ig.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL

const PILAR_LABEL = {
  costo_invisible: '💸 Costo invisible',
  diccionario:     '📖 Diccionario',
  diario_barrio:   '🗺️ Diario de barrio',
  expectativa:     '⏳ Expectativa',
  lanzamiento:     '🚀 Lanzamiento',
  retencion:       '💎 Retención',
  otro:            '📝 Otro',
}

const FORMATO_LABEL = {
  carrusel:    '🎠 Carrusel',
  post_simple: '📷 Post',
  story:       '⚡ Story',
  reel:        '🎬 Reel',
}

const ESTADO_BADGE = {
  borrador:   { label: 'Borrador',   bg: '#fef3c7', fg: '#92400e' },
  generado:   { label: 'Generado',   bg: '#dbeafe', fg: '#1e3a8a' },
  aprobado:   { label: 'Aprobado',   bg: '#dcfce7', fg: '#166534' },
  programado: { label: 'Programado', bg: '#e0e7ff', fg: '#3730a3' },
  publicado:  { label: 'Publicado',  bg: '#f3e8ff', fg: '#6b21a8' },
  rechazado:  { label: 'Rechazado',  bg: '#fee2e2', fg: '#991b1b' },
}

const EJEMPLOS = [
  'Lanzamos en 6 días — necesito 2 piezas: una de expectativa hoy y una contando que abrimos el martes 12.',
  'Quiero un carrusel para Pocitos contando que cubrimos el barrio. 4 slides.',
  'Story explicando que con la suscripción ahorrás 10% vs súper. Tono cotidiano.',
  'Post de testimonio con la frase de Florencia ("Me llega el día que pido y ya está").',
]

export default function MarketingAgent() {
  const [brief, setBrief] = useState('')
  const [contextoExtra, setContextoExtra] = useState('')
  const [showContextoExtra, setShowContextoExtra] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [trace, setTrace] = useState([])
  const [showTrace, setShowTrace] = useState(false)
  const [posts, setPosts] = useState([])
  const [loadingPosts, setLoadingPosts] = useState(true)

  useEffect(() => { cargarPosts() }, [])

  async function cargarPosts() {
    setLoadingPosts(true)
    // Solo piezas activas (generado / aprobado / publicando) — sin rechazadas viejas mezcladas
    const { data } = await supabase
      .from('posts_ig')
      .select('id, brief, pilar, formato, estado, cantidad_slides, caption, hashtags, slides, horario_sugerido, programado_para, created_at, imagenes_renders, calendario_id')
      .in('estado', ['generado', 'aprobado', 'publicando', 'borrador'])
      .order('horario_sugerido', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(30)
    setPosts(data || [])
    setLoadingPosts(false)
  }

  async function enviarBrief({ auto = false } = {}) {
    if (!auto && brief.trim().length < 5) {
      setError('Escribime un brief de al menos 5 caracteres.')
      return
    }
    setEnviando(true)
    setError(null)
    setResultado(null)
    setTrace([])
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sin sesión activa.')

      const payload = auto
        ? { auto: true }
        : { brief: brief.trim(), contexto_extra: contextoExtra.trim() || undefined }

      const res = await fetch(`${SUPA_URL}/functions/v1/gerente-marketing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || 'El agente respondió con error')

      setResultado(json)
      setTrace(json.trace || [])
      await cargarPosts()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setEnviando(false)
    }
  }

  async function renderizarImagenes(postId) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      const res = await fetch(`${SUPA_URL}/functions/v1/renderizar-post-ideogram`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: postId }),
      })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || 'Error renderizando')
      let msg = `Listo: ${json.count}/${json.total} slides renderizados con Ideogram.`
      if (json.errors && json.errors.length > 0) {
        msg += '\n\n⚠️ Errores por slide:\n' + json.errors.map(e => `  Slide ${e.index + 1}: ${e.error}`).join('\n')
      }
      alert(msg)
      await cargarPosts()
    } catch (e) {
      alert('Error: ' + e.message)
    }
  }

  async function aprobarPost(postId) {
    const { error } = await supabase
      .from('posts_ig')
      .update({ estado: 'aprobado', approved_at: new Date().toISOString() })
      .eq('id', postId)
    if (error) { alert('Error: ' + error.message); return }
    await cargarPosts()
  }

  async function rechazarPost(postId) {
    if (!confirm('¿Rechazar esta pieza? Queda guardada como rechazada.')) return
    const { error } = await supabase
      .from('posts_ig')
      .update({ estado: 'rechazado' })
      .eq('id', postId)
    if (error) { alert('Error: ' + error.message); return }
    await cargarPosts()
  }

  return (
    <AdminLayout title="🎩 Gerente de Marketing">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px' }}>

        {/* Hero / explicación */}
        <div style={{
          background: 'linear-gradient(135deg, #1A3461 0%, #1A3461 100%)',
          color: 'white', padding: 24, borderRadius: 12, marginBottom: 20,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#00D4E0', marginBottom: 6 }}>
            Agente Limpack
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, letterSpacing: '-.02em' }}>
            Decime qué hay que <span style={{ color: '#00D4E0' }}>comunicar</span>.
          </h1>
          <p style={{ margin: 0, opacity: .85, lineHeight: 1.5 }}>
            Yo decido formato, pilar, copy y slides — siguiendo el manual de marca v2.0. Vos aprobás o pedís cambios.
          </p>
        </div>

        {/* Brief input */}
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#0F172A' }}>
            ¿Qué necesitamos comunicar?
          </label>
          <textarea
            value={brief}
            onChange={e => setBrief(e.target.value)}
            placeholder="Ej: Lanzamos el martes 12. Necesito 2 piezas: una de expectativa para mañana y una de lanzamiento para el martes."
            disabled={enviando}
            style={{
              width: '100%', minHeight: 100, padding: 12, fontSize: 15, lineHeight: 1.5,
              border: '1px solid #CBD5E1', borderRadius: 8, fontFamily: 'inherit', resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />

          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => setShowContextoExtra(!showContextoExtra)}
              style={{ background: 'none', border: 'none', color: '#00A8B5', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              {showContextoExtra ? '− Ocultar' : '+ Agregar'} contexto extra (datos, frases textuales, etc.)
            </button>
          </div>

          {showContextoExtra && (
            <textarea
              value={contextoExtra}
              onChange={e => setContextoExtra(e.target.value)}
              placeholder="Ej: La frase exacta del testimonio. O el dato concreto a usar. O el barrio puntual."
              style={{
                width: '100%', minHeight: 70, padding: 12, fontSize: 14, marginTop: 8,
                border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'inherit', resize: 'vertical',
                boxSizing: 'border-box', background: '#F8FAFC',
              }}
            />
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => enviarBrief()}
              disabled={enviando || brief.trim().length < 5}
              style={{
                background: enviando ? '#94A3B8' : '#00A8B5',
                color: 'white', border: 'none', borderRadius: 8,
                padding: '12px 24px', fontSize: 15, fontWeight: 700, cursor: enviando ? 'wait' : 'pointer',
                letterSpacing: '-.01em',
              }}
            >
              {enviando ? '🔄 El agente está trabajando…' : '🚀 Enviar al agente'}
            </button>
            <button
              onClick={() => {
                if (!confirm('¿Generar 1-3 propuestas automáticamente?\n\nEl Gerente va a mirar el calendario editorial, los últimos posts y la etapa de pre-launch, y decide solo qué proponer. No tenés que escribir nada.')) return
                enviarBrief({ auto: true })
              }}
              disabled={enviando}
              style={{
                background: enviando ? '#94A3B8' : '#1A3461',
                color: 'white', border: 'none', borderRadius: 8,
                padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: enviando ? 'wait' : 'pointer',
              }}
              title="El Gerente decide solo qué generar hoy, basado en calendario + histórico + etapa"
            >
              🤖 Generar automáticas
            </button>
            {brief.length > 0 && !enviando && (
              <button
                onClick={() => { setBrief(''); setContextoExtra(''); setError(null); setResultado(null); setTrace([]) }}
                style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer' }}
              >
                Limpiar
              </button>
            )}
          </div>

          {/* Ejemplos */}
          {!brief && !enviando && !resultado && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #E2E8F0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                Ejemplos de briefs
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {EJEMPLOS.map((ej, i) => (
                  <button
                    key={i}
                    onClick={() => setBrief(ej)}
                    style={{
                      background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6,
                      padding: '8px 12px', textAlign: 'left', cursor: 'pointer', fontSize: 13,
                      color: '#334155', lineHeight: 1.4,
                    }}
                  >
                    {ej}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: 14, borderRadius: 8, marginBottom: 16 }}>
            <strong>Error del agente:</strong> {error}
          </div>
        )}

        {/* Resultado */}
        {resultado && (
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>✅</span>
              <strong style={{ color: '#166534', fontSize: 16 }}>Listo. {resultado.piezas_creadas?.length || 0} pieza(s) creadas en {resultado.iteraciones} iteración(es).</strong>
            </div>
            <p style={{ margin: '8px 0', color: '#14532D', lineHeight: 1.5 }}>{resultado.resumen}</p>
            {resultado.siguiente_paso && (
              <div style={{ background: 'white', borderRadius: 6, padding: '8px 12px', marginTop: 10, fontSize: 13, color: '#334155' }}>
                <strong>Siguiente paso:</strong> {resultado.siguiente_paso}
              </div>
            )}
            {trace.length > 0 && (
              <button
                onClick={() => setShowTrace(!showTrace)}
                style={{ background: 'none', border: 'none', color: '#00A8B5', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginTop: 10 }}
              >
                {showTrace ? '▾ Ocultar' : '▸ Ver'} trace del agente ({trace.length} pasos)
              </button>
            )}
            {showTrace && (
              <div style={{ background: '#0F172A', color: '#E2E8F0', padding: 12, borderRadius: 6, marginTop: 8, fontSize: 11, fontFamily: 'monospace', maxHeight: 400, overflow: 'auto' }}>
                {trace.map((t, i) => (
                  <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #334155' }}>
                    <strong style={{ color: t.tipo === 'pensamiento' ? '#5BBDD8' : t.tipo === 'tool_use' ? '#00D4E0' : '#A7F3D0' }}>
                      [{t.tipo}{t.tool ? ` · ${t.tool}` : ''}]
                    </strong>
                    <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {t.tipo === 'pensamiento' ? t.texto : JSON.stringify(t.input || t.output, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lista de posts recientes */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0F172A' }}>📋 Piezas recientes</h2>
            <button
              onClick={cargarPosts}
              disabled={loadingPosts}
              style={{ background: 'none', border: '1px solid #CBD5E1', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#475569' }}
            >
              {loadingPosts ? '...' : '🔄 Recargar'}
            </button>
          </div>

          {loadingPosts && <div style={{ color: '#64748B', padding: 20 }}>Cargando…</div>}
          {!loadingPosts && posts.length === 0 && (
            <div style={{ background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 8, padding: 30, textAlign: 'center', color: '#64748B' }}>
              Todavía no hay piezas. Mandale un brief al agente.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {posts.map(p => {
              const badge = ESTADO_BADGE[p.estado] || { label: p.estado, bg: '#E2E8F0', fg: '#475569' }
              const renders = p.imagenes_renders?.ideogram || []
              const yaRenderizado = renders.some(u => !!u)
              return (
                <div key={p.id} style={{ background: 'white', border: p.horario_sugerido ? '2px solid #00A8B5' : '1px solid #E2E8F0', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {p.horario_sugerido && (
                    <div style={{ background: 'linear-gradient(90deg, #1A3461, #00A8B5)', color: 'white', padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>📅</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {new Date(p.horario_sugerido).toLocaleDateString('es-UY', { weekday: 'short', day: '2-digit', month: 'short' })}
                      </span>
                      <span style={{ opacity: 0.85 }}>·</span>
                      <span>{new Date(p.horario_sugerido).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false })}hs</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', background: badge.bg, color: badge.fg, borderRadius: 999 }}>
                        {badge.label}
                      </span>
                      <span style={{ fontSize: 11, color: '#64748B' }}>
                        {FORMATO_LABEL[p.formato] || p.formato} · {PILAR_LABEL[p.pilar] || p.pilar}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: '#94A3B8' }}>
                      gen {new Date(p.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>

                  <div style={{ fontSize: 13, color: '#0F172A', fontWeight: 600, lineHeight: 1.3 }}>
                    {p.brief}
                  </div>

                  {Array.isArray(p.slides) && p.slides.length > 0 && (
                    <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: 8, fontSize: 12, color: '#475569', maxHeight: 110, overflow: 'auto' }}>
                      {p.slides.map((s, i) => (
                        <div key={i} style={{ marginBottom: i < p.slides.length - 1 ? 6 : 0 }}>
                          <strong style={{ color: '#1A3461' }}>S{i + 1}</strong> · <em style={{ color: '#64748B' }}>{s.template}</em><br />
                          <span>{(s.titulo || '').replace(/\*\*/g, '')}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {p.caption && (
                    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 6, padding: 8, fontSize: 12, lineHeight: 1.45, color: '#334155', whiteSpace: 'pre-wrap' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', marginBottom: 4, letterSpacing: 0.5 }}>CAPTION IG</div>
                      {p.caption.length > 240 ? p.caption.slice(0, 240) + '…' : p.caption}
                    </div>
                  )}

                  {Array.isArray(p.hashtags) && p.hashtags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {p.hashtags.slice(0, 14).map((h, i) => (
                        <span key={i} style={{ fontSize: 10, fontWeight: 600, color: '#00A8B5', background: '#E0F7FA', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                          {h.startsWith('#') ? h : '#' + h}
                        </span>
                      ))}
                      {p.hashtags.length > 14 && (
                        <span style={{ fontSize: 10, color: '#94A3B8', alignSelf: 'center' }}>+{p.hashtags.length - 14}</span>
                      )}
                    </div>
                  )}

                  {yaRenderizado && (
                    <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
                      {renders.filter(Boolean).slice(0, 3).map((url, i) => (
                        <img key={i} src={url} alt={`slide ${i+1}`} style={{ width: 70, height: 88, objectFit: 'cover', borderRadius: 4, border: '1px solid #E2E8F0', flexShrink: 0 }} />
                      ))}
                    </div>
                  )}

                  {p.horario_sugerido && (
                    <div style={{ fontSize: 11, color: '#64748B' }}>
                      📅 Sugerido: {new Date(p.horario_sugerido).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
                    {!yaRenderizado && p.estado !== 'rechazado' && (
                      <button
                        onClick={() => renderizarImagenes(p.id)}
                        style={{ background: '#5BBDD8', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        🎨 Renderizar
                      </button>
                    )}
                    {p.estado !== 'aprobado' && p.estado !== 'rechazado' && (
                      <button
                        onClick={() => aprobarPost(p.id)}
                        style={{ background: '#16A34A', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        ✓ Aprobar
                      </button>
                    )}
                    {p.estado !== 'rechazado' && (
                      <button
                        onClick={() => rechazarPost(p.id)}
                        style={{ background: 'white', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        ✗ Rechazar
                      </button>
                    )}
                    <Link
                      to={`/admin/calendario-marketing`}
                      style={{ background: 'none', border: '1px solid #CBD5E1', color: '#475569', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                    >
                      📋 Ver detalle
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

      </div>
    </AdminLayout>
  )
}
